import { toCamelCaseRecursive } from '@sv-support-sdk/utils/src/helpers/string';
import axios, {
  AxiosInstance,
  AxiosResponse,
  CreateAxiosDefaults,
  InternalAxiosRequestConfig,
} from 'axios';

type RequestInterceptor = (
  value: InternalAxiosRequestConfig<any>,
) => InternalAxiosRequestConfig<any> | Promise<InternalAxiosRequestConfig<any>>;

type RejectInterceptor = ((error: any) => any) | null;

type ResponseInterceptor = (
  value: AxiosResponse<any, any>,
) => AxiosResponse<any, any> | Promise<AxiosResponse<any, any>>;

interface InterceptorsProps {
  request?: {
    onFullfilled?: RequestInterceptor;
    onRejected?: RejectInterceptor;
  };
  response?: {
    onFullfilled?: ResponseInterceptor;
    onRejected?: RejectInterceptor;
  };
};

export default class Api {
  options: CreateAxiosDefaults;
  instance: AxiosInstance | null = null;
  interceptors: Function[] = [];

  constructor(options: CreateAxiosDefaults) {
    this.options = options;
  }

  setupInstance({ interceptors }: { interceptors?: InterceptorsProps } = {}) {
    const defaultHeaders = {
      'Content-Type': 'application/json',
    };

    this.instance = axios.create({
      headers: defaultHeaders,
      ...this.options,
    });

    this.setInterceptors(interceptors);

    return this.instance;
  }

  defaultResponseHandling() {
    this.instance?.interceptors.response.use(
      (response) => {
        if (response?.data?.code === 400) {
          return Promise.reject(response.data);
        }

        if (response?.data?.errors && !response?.data?.ok) {
          return Promise.reject({ message: response.data.errors });
        }

        return Promise.resolve(toCamelCaseRecursive(response.data));
      },
      (error) => Promise.reject(error),
    );
  }

  setInterceptors({
    request,
    response,
  }: InterceptorsProps = {}) {
    const isAnyRequestInterceptor = request?.onFullfilled || request?.onRejected;
    const isAnyResponseInterceptor = response?.onFullfilled || response?.onRejected;

    if (isAnyRequestInterceptor) {
      this.instance?.interceptors.request.use(request.onFullfilled, request.onRejected);
    }

    if (isAnyResponseInterceptor) {
      this.instance?.interceptors.response.use(response.onFullfilled, response.onRejected);
    } else {
      this.defaultResponseHandling();
    }
  }
}
